import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { Campaign, ProspectCatalogs } from "../../../services/v2/prospects.service";
import { CarteraSelect } from "./carteraView";
import { cuando, errorTexto, ETAPAS, OBJETIVOS_BARRA, vistasDeEtapa } from "./salesHelpers";

interface Props { campusId: number; catalogs: ProspectCatalogs | null; onSalir: () => void; }

/** Los factores del puntaje del marcador, como los entiende una supervisora. 1 = como está; 0 = no cuenta; 2 = el doble. */
export const PESOS: { key: string; label: string; ayuda: string }[] = [
	{ key: "due", label: "Seguimientos que vencen", ayuda: "agendados para hoy o ya vencidos" },
	{ key: "wrote", label: "Escribió hace poco", ayuda: "últimas 24 h, 3 o 7 días" },
	{ key: "fresh", label: "Recién llegados", ayuda: "nuevos de hoy, ayer o esta semana" },
	{ key: "status", label: "Status", ayuda: "Promesa, Recontactó, Interesado" },
	{ key: "engaged", label: "Mostró interés", ayuda: "muchos mensajes o mensajes largos" },
	{ key: "never", label: "Nunca llamados", ayuda: "todavía no tienen llamada" },
	{ key: "penalty", label: "Castigo por no contestar", ayuda: "baja a quien no contesta seguido" },
];
const DIAS = [{ d: 1, t: "L" }, { d: 2, t: "M" }, { d: 3, t: "Mi" }, { d: 4, t: "J" }, { d: 5, t: "V" }, { d: 6, t: "S" }, { d: 7, t: "D" }];
const FLAGS = [{ key: "sin_nota_30", label: "Sin nota 30 d" }, { key: "vencidas", label: "Actividad vencida" }, { key: "califican_promo", label: "Califica a promo" }];
const ESTADO_TXT: { [k: string]: string } = { borrador: "Borrador", activa: "Activa", pausada: "Pausada", terminada: "Terminada" };

const nueva = (): any => ({
	name: "", objetivo: "", mode: "personal", pacing: "vista_previa", callerId: "",
	filters: { owner: "all", etapa: 1, status: null, objetivo: "", view: "", flags: [], curso: "" },
	agents: [], schedule: { days: [1, 2, 3, 4, 5, 6], from: "09:00", to: "19:00" }, retry: { no_answer_hours: 3, max_attempts: 4 },
	weights: Object.fromEntries(PESOS.map((p) => [p.key, 1])),
});

/**
 * Campañas del marcador (supervisión y dirección): a quién llamar, quién marca y cómo se reparte, cuándo, cuántos
 * reintentos, qué pesa más en el orden y qué número ve el cliente. La asesora las ve en su Marcador.
 */
const CampanasView: React.FC<Props> = ({ campusId, catalogs, onSalir }) => {
	const http = HttpManager.getInstance();
	const [rows, setRows] = useState<Campaign[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [editar, setEditar] = useState<any | null>(null);
	const [detalle, setDetalle] = useState<any | null>(null);

	const cargar = () => http.prospectsService.campaigns(campusId).then((r) => setRows(r.data.campaigns)).catch((e) => setError(errorTexto(e)));
	useEffect(() => { cargar(); }, [campusId]);

	const estado = async (c: Campaign, status: string) => {
		setError(null);
		if (status === "terminada" && !window.confirm(`¿Terminar «${c.name}»? Ya no se podrá volver a activar.`)) return;
		try { await http.prospectsService.campaignStatus(campusId, c.id, status); cargar(); if (detalle?.campaign?.id === c.id) verDetalle(c); }
		catch (e) { setError(errorTexto(e)); }
	};
	const verDetalle = async (c: Campaign) => {
		try { setDetalle((await http.prospectsService.campaign(campusId, c.id)).data); } catch (e) { setError(errorTexto(e)); }
	};
	const abrirEditar = (c: Campaign | null) => setEditar(c ? {
		id: c.id, name: c.name, objetivo: c.objetivo || "", mode: c.mode, pacing: c.pacing, callerId: c.caller_id || "",
		filters: { ...nueva().filters, ...(c.filters || {}) }, agents: c.agents || [], schedule: c.schedule || nueva().schedule,
		retry: c.retry || nueva().retry, weights: { ...nueva().weights, ...(c.weights || {}) }, status: c.status,
	} : nueva());

	return (
		<div className="tb">
			<div className="tb-cab">
				<div><h2>Campañas del marcador</h2><span className="apagado">Las asesoras las ven en su Marcador. Una campaña activa congela su lista.</span></div>
				<div className="tb-herr">
					<button className="btn-wa pri" onClick={() => abrirEditar(null)}>Nueva campaña</button>
					<button className="enlace" onClick={onSalir}>Volver a la bandeja</button>
				</div>
			</div>
			{error ? <div className="aviso error" style={{ margin: "10px 18px 0" }}>{error}</div> : null}
			<div className="tb-cuerpo">
				{!rows ? <div className="ventas-vacio">Cargando…</div> : !rows.length ? <div className="ventas-vacio">Todavía no hay campañas. Crea la primera con «Nueva campaña».</div> : (
					<div className="tarjeta">
						<table className="tb-tabla">
							<thead><tr><th>Campaña</th><th>Estado</th><th>Cómo</th><th>Asesoras</th><th>Avance</th><th>Contestaron</th><th /></tr></thead>
							<tbody>
								{rows.map((c) => {
									const st = c.stats || { total: 0, hechos: 0, fuera: 0, contestaron: 0, intentos: 0 };
									const pct = st.total ? Math.round(100 * (st.hechos + st.fuera) / st.total) : 0;
									return (
										<tr key={c.id}>
											<td><button className="ct-nombre" onClick={() => verDetalle(c)}>{c.name}</button><div className="apagado ct-sub">{c.created_by_name} · {cuando(c.created_at)}</div></td>
											<td><span className={"chip " + (c.status === "activa" ? "verde" : c.status === "pausada" ? "oro" : "")}>{ESTADO_TXT[c.status]}</span></td>
											<td className="apagado">{c.mode === "grupal" ? "Grupal" : "Personal"} · {c.pacing === "automatico" ? "automático" : "vista previa"}</td>
											<td>{c.agentes ?? "—"}</td>
											<td><div className="tb-barra"><span style={{ width: pct + "%" }} /></div><span className="apagado ct-sub">{st.hechos + st.fuera} de {st.total || c.contacts_total || "—"}</span></td>
											<td>{st.contestaron}{st.intentos ? <span className="apagado"> · {Math.round(100 * st.contestaron / st.intentos)}%</span> : null}</td>
											<td style={{ whiteSpace: "nowrap" }}>
												{c.status !== "activa" ? <button className="btn-wa pri mini" onClick={() => estado(c, "activa")}>{c.status === "borrador" ? "Activar" : "Reanudar"}</button> : null}
												{c.status === "activa" ? <button className="btn-wa mini" onClick={() => estado(c, "pausada")}>Pausar</button> : null}
												{c.status === "borrador" ? <button className="btn-wa mini" onClick={() => abrirEditar(c)}>Editar</button> : null}
												<button className="btn-wa mini" onClick={() => estado(c, "terminada")}>Terminar</button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
				{detalle ? (
					<div className="tb-grid">
						<div className="tarjeta">
							<div className="tarjeta-cab"><h4>{detalle.campaign.name} · por asesora</h4><button className="enlace" onClick={() => setDetalle(null)}>Cerrar</button></div>
							<table className="tb-tabla">
								<thead><tr><th>Asesora</th><th>Lista</th><th>Hechas</th><th>Fuera</th><th>Contestaron</th><th>Intentos</th></tr></thead>
								<tbody>{detalle.porAsesora.map((a: any, i: number) => (
									<tr key={i}><td>{a.asesora || (detalle.campaign.mode === "grupal" ? "Fila del equipo" : "Sin asesora")}</td><td>{a.total}</td><td>{a.hechos}</td><td>{a.fuera}</td><td>{a.contestaron}</td><td>{a.intentos}</td></tr>
								))}</tbody>
							</table>
						</div>
						<div className="tarjeta">
							<h4>Resultados</h4>
							{Object.entries(detalle.resultados || {}).map(([k, v]) => <div key={k} className="par"><span>{k.replace("_", " ")}</span><span>{String(v)}</span></div>)}
							{!Object.keys(detalle.resultados || {}).length ? <span className="apagado">Todavía sin llamadas.</span> : null}
						</div>
					</div>
				) : null}
			</div>
			{editar ? <EditorCampana campusId={campusId} catalogs={catalogs} valor={editar} onClose={() => setEditar(null)} onGuardada={() => { setEditar(null); cargar(); }} /> : null}
		</div>
	);
};

const EditorCampana: React.FC<{ campusId: number; catalogs: ProspectCatalogs | null; valor: any; onClose: () => void; onGuardada: () => void }> = ({ campusId, catalogs, valor, onClose, onGuardada }) => {
	const http = HttpManager.getInstance();
	const [v, setV] = useState<any>(valor);
	const [cuantos, setCuantos] = useState<number | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const set = (k: string, x: any) => setV({ ...v, [k]: x });
	const setF = (k: string, x: any) => { setV({ ...v, filters: { ...v.filters, [k]: x } }); setCuantos(null); };
	const toggle = (arr: any[], x: any) => (arr.includes(x) ? arr.filter((y) => y !== x) : [...arr, x]);
	const payload = () => ({ ...v, objetivo: v.filters.objetivo || null, filters: { ...v.filters, status: v.filters.status ? [v.filters.status] : undefined,
		view: v.filters.view || undefined, objetivo: v.filters.objetivo || undefined, curso: v.filters.curso || undefined } });

	const contar = async () => {
		setError(null);
		try { setCuantos((await http.prospectsService.previewCampaign(campusId, payload())).data.count); } catch (e) { setError(errorTexto(e)); }
	};
	const guardar = async (activar: boolean) => {
		setBusy(true); setError(null);
		try {
			const r = await http.prospectsService.saveCampaign(campusId, v.id || null, payload());
			if (activar) await http.prospectsService.campaignStatus(campusId, r.data.campaign.id, "activa");
			onGuardada();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};

	const porEquipo: { [t: string]: { id: string; name: string }[] } = {};
	for (const a of catalogs?.advisors || []) (porEquipo[a.team || "Otras"] = porEquipo[a.team || "Otras"] || []).push(a);
	const vistas = vistasDeEtapa(Number(v.filters.etapa) || 1);

	return (
		<Modal show onHide={onClose} size="xl" className="ventas-nuevo-modal">
			<Modal.Header closeButton><Modal.Title style={{ fontFamily: "Bricolage, sans-serif", fontWeight: 800 }}>{v.id ? "Editar campaña" : "Nueva campaña"}</Modal.Title></Modal.Header>
			<Modal.Body>
				<div className="cp-rejilla">
					<div className="cp-col">
						<div className="tarjeta">
							<h4>Campaña</h4>
							<label htmlFor="cp-n">Nombre</label>
							<input id="cp-n" value={v.name} onChange={(e) => set("name", e.target.value)} placeholder="Promo 6x2 matriculadas de septiembre" />
							<label htmlFor="cp-cid">Número que ve el cliente</label>
							<input id="cp-cid" value={v.callerId} onChange={(e) => set("callerId", e.target.value)} placeholder="el del plantel (si se deja vacío)" />
						</div>
						<div className="tarjeta">
							<h4>A quién se llama</h4>
							<label>Cartera</label>
							<CarteraSelect catalogs={catalogs} value={v.filters.owner} onChange={(x) => setF("owner", x)} />
							<div className="cp-dos">
								<div><label>Etapa</label>
									<select value={v.filters.etapa || ""} onChange={(e) => setV({ ...v, filters: { ...v.filters, etapa: e.target.value ? Number(e.target.value) : null, view: "" } })}>
										<option value="">Todas</option>{ETAPAS.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
									</select></div>
								<div><label>Status</label>
									<select value={v.filters.status || ""} onChange={(e) => setF("status", e.target.value ? Number(e.target.value) : null)}>
										<option value="">Todos</option>{(catalogs?.statuses || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
									</select></div>
								<div><label>Objetivo</label>
									<select value={v.filters.objetivo || ""} onChange={(e) => setF("objetivo", e.target.value)}>
										<option value="">Todos</option>{OBJETIVOS_BARRA.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
									</select></div>
								<div><label>Vista</label>
									<select value={v.filters.view || ""} onChange={(e) => setF("view", e.target.value)}>
										<option value="">Cualquiera</option>
										{vistas.map((g) => <optgroup key={g.grupo} label={g.grupo}>{g.items.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}</optgroup>)}
									</select></div>
							</div>
							<label>Curso</label>
							<input value={v.filters.curso || ""} onChange={(e) => setF("curso", e.target.value)} placeholder="repostería, barbería…" />
							<div className="cp-chips">{FLAGS.map((f) => (
								<button key={f.key} type="button" className={"btn-wa mini" + (v.filters.flags.includes(f.key) ? " on" : "")} aria-pressed={v.filters.flags.includes(f.key)}
									onClick={() => setF("flags", toggle(v.filters.flags, f.key))}>{f.label}</button>
							))}</div>
							<div className="fila-botones"><button className="btn-wa" type="button" onClick={contar}>¿Cuántos entran?</button>
								{cuantos !== null ? <b>{cuantos.toLocaleString("es-MX")} personas{cuantos >= 5000 ? " (tope 5,000)" : ""}</b> : null}</div>
						</div>
					</div>

					<div className="cp-col">
						<div className="tarjeta">
							<h4>Quién marca</h4>
							<div className="cp-dos">
								<div><label>Reparto</label>
									<select value={v.mode} onChange={(e) => set("mode", e.target.value)}>
										<option value="personal">Personal: cada quien su gente</option>
										<option value="grupal">Grupal: una fila para todas</option>
									</select></div>
								<div><label>Ritmo</label>
									<select value={v.pacing} onChange={(e) => set("pacing", e.target.value)}>
										<option value="vista_previa">Vista previa: ve la ficha y da Llamar</option>
										<option value="automatico">Automático: marca sola al quedar libre</option>
									</select></div>
							</div>
							<div className="cp-agentes">
								{Object.keys(porEquipo).sort().map((t) => (
									<div key={t} className="cp-equipo">
										<label className="cp-check"><input type="checkbox" checked={v.agents.includes("team:" + t)} onChange={() => set("agents", toggle(v.agents, "team:" + t))} /><b>Equipo {t} completo</b></label>
										{!v.agents.includes("team:" + t) ? porEquipo[t].map((a) => (
											<label key={a.id} className="cp-check"><input type="checkbox" checked={v.agents.includes("z:" + a.id)} onChange={() => set("agents", toggle(v.agents, "z:" + a.id))} />{a.name}</label>
										)) : null}
									</div>
								))}
							</div>
						</div>
						<div className="tarjeta">
							<h4>Cuándo y reintentos</h4>
							<div className="cp-chips">{DIAS.map((d) => (
								<button key={d.d} type="button" className={"btn-wa mini" + (v.schedule.days.includes(d.d) ? " on" : "")} aria-pressed={v.schedule.days.includes(d.d)}
									onClick={() => set("schedule", { ...v.schedule, days: toggle(v.schedule.days, d.d).sort() })}>{d.t}</button>
							))}</div>
							<div className="cp-dos">
								<div><label>Desde</label><input type="time" value={v.schedule.from} onChange={(e) => set("schedule", { ...v.schedule, from: e.target.value })} /></div>
								<div><label>Hasta</label><input type="time" value={v.schedule.to} onChange={(e) => set("schedule", { ...v.schedule, to: e.target.value })} /></div>
								<div><label>Si no contesta, reintentar en (horas)</label><input type="number" min={1} max={168} value={v.retry.no_answer_hours} onChange={(e) => set("retry", { ...v.retry, no_answer_hours: Number(e.target.value) })} /></div>
								<div><label>Intentos como máximo</label><input type="number" min={1} max={10} value={v.retry.max_attempts} onChange={(e) => set("retry", { ...v.retry, max_attempts: Number(e.target.value) })} /></div>
							</div>
							<span className="apagado">Hora de México. Contestó o número equivocado: sale de la lista.</span>
						</div>
						<div className="tarjeta">
							<h4>Qué pesa más en el orden</h4>
							{PESOS.map((p) => (
								<div key={p.key} className="cp-peso">
									<label htmlFor={"cp-w-" + p.key}>{p.label}<span className="apagado"> · {p.ayuda}</span></label>
									<input id={"cp-w-" + p.key} type="range" min={0} max={2} step={0.25} value={v.weights[p.key]} onChange={(e) => set("weights", { ...v.weights, [p.key]: Number(e.target.value) })} />
									<span className="cp-x">{v.weights[p.key] === 0 ? "no cuenta" : "×" + v.weights[p.key]}</span>
								</div>
							))}
						</div>
					</div>
				</div>
				{error ? <div className="aviso error" style={{ marginTop: 10 }}>{error}</div> : null}
			</Modal.Body>
			<Modal.Footer>
				<button className="btn-wa" onClick={onClose}>Cancelar</button>
				<button className="btn-wa" disabled={busy || !v.name.trim() || !v.agents.length} onClick={() => guardar(false)}>Guardar borrador</button>
				{!v.id || v.status === "borrador" ? <button className="btn-wa pri" disabled={busy || !v.name.trim() || !v.agents.length} onClick={() => guardar(true)}>Guardar y activar</button> : null}
			</Modal.Footer>
		</Modal>
	);
};

export default CampanasView;
